<template>
  <div data-preview-layout-v="1.0.0" class="page">
    <slot />
    <div id="teleports" />

    <div
      v-if="globalStore.isInspectorMode && globalStore.pageEntrySys?.id"
      class="global-page-inspector"
    >
      <div class="page-inspector-badge">
        <span class="badge-type">
          Page ID:
          <span class="badge-base-type">{{
            globalStore.pageEntrySys?.contentType?.sys?.id || 'Page'
          }}</span>
        </span>
        <a
          :href="`https://app.contentful.com/spaces/0piqveu8x9oj/entries/${globalStore.pageEntrySys.id}`"
          target="_blank"
          class="badge-link"
          title="Open Contentful Page Entry"
        >
          {{ globalStore.pageEntrySys.id }}
          <IconExternalLink width="12" height="12" />
        </a>
        <EntryStatus
          v-if="globalStore.pageEntrySys"
          :status="getEntryStatus(globalStore.pageEntrySys)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getEntryStatus } from '~/utils/contentfulStatus'

type WebComponentsRegistryWindow = Window &
  typeof globalThis & {
    WebComponentsRegistry?: {
      setContentfulConfig: (config: {
        spaceId: string
        accessToken: string
      }) => void
      register: (elements: string[]) => void
      registerAll?: () => void
    }
  }

const globalStore = useGlobalStore()

const loadCustomElementScript = () => {
  const hash = 'D2Oz9ym_'
  const fileName = `web-components-registry.${hash}`

  const scriptExists = document.querySelector(
    `script[src="/js/${fileName}.js"]`,
  )

  if (!scriptExists) {
    const script = document.createElement('script')
    script.src = `/js/${fileName}.js`
    script.type = 'text/javascript'
    script.onload = () => {
      const registryWindow = window as WebComponentsRegistryWindow

      if (registryWindow.WebComponentsRegistry) {
        const { setContentfulConfig, register } =
          registryWindow.WebComponentsRegistry

        setContentfulConfig({
          spaceId: 's5n2t79q9icq',
          accessToken: 'poEmXFXQhqamkmkKDV0cDt-SeuT2He6QWwlk7mGCOtM',
        })

        register(['auto-card', 'magic-card', 'magic-cards', 'mana-icon'])
      } else {
        console.warn('WebComponentsRegistry is not available on window')
      }
    }
    document.body.appendChild(script)
  }
}

onMounted(() => {
  onNuxtReady(() => loadCustomElementScript())
})
</script>

<style lang="scss">
:root {
  --black: #000;
  --white: #fff;

  --purple: #504e8b;
  --purple-light: #7a79c1;
  --orange: #e73511;
  --orange-light: #ee7158;

  --green: #0b4a1d;

  --html-background: var(--gray-1, #f5f5f5);

  --base-font-family: 'Open Sans', Arial, sans-serif;
  --base-font-size: 16px;

  --roboto-condensed: 'Roboto Condensed', Arial, serif;
}

html {
  background: var(--html-background);
  box-sizing: border-box;
  color: var(--black);
  font-family: var(--base-font-family);
  font-size: var(--base-font-size);
  -ms-text-size-adjust: 100%;
  -webkit-text-size-adjust: 100%;
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;
}

*,
*:before,
*:after {
  box-sizing: border-box;
  margin: 0;
}

.page {
  margin: auto;
  max-width: 1920px;
}

a {
  color: var(--purple-light);
  text-decoration: none;
}

.global-page-inspector {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  pointer-events: none;
}

.page-inspector-badge {
  pointer-events: auto;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 2px solid var(--purple);
  border-radius: 8px;
  padding: 8px 14px;
  display: flex;
  align-items: center;
  gap: 16px;
  font-family: var(--base-font-family);
  font-size: 14px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.page-inspector-badge:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
}

.global-page-inspector .badge-type {
  font-weight: 600;
  color: #333;
}

.global-page-inspector .badge-base-type {
  color: var(--purple);
  font-weight: 700;
  text-transform: capitalize;
}

.global-page-inspector .badge-link {
  color: var(--purple);
  text-decoration: none;
  font-family: monospace;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.global-page-inspector .badge-link:hover {
  text-decoration: underline;
}
</style>
